import type { LucideIcon } from "lucide-react";
import {
  Image as ImageIcon,
  Video as VideoIcon,
  Copy,
  Sparkles,
  Flame,
  Compass,
} from "lucide-react";
import type { CompanionPromptDisplay } from "@/shared/lib/events";

export type MarcusSlashCommandCategory = "static-ads" | "video-ads" | "onboarding";

/**
 * How the slash command should be carried out once selected.
 *
 * - `send-prompt` (default): the standard flow — the hidden prompt is sent
 *   to Marcus as a managed user message and the command bubble renders from
 *   the same message's `data-companion-command` part. Marcus then calls the
 *   matching `ads_dispatch_*` tool.
 * - `publish-onboarding-start`: skip the Marcus round-trip entirely. The
 *   handler injects a local-only command bubble into the chat and publishes
 *   the `marcus-onboarding:start-direct` event, which the onboarding
 *   controller turns into a `startOnboarding(...)` call (no dialog popup).
 *   We do not send a hidden prompt because `startOnboarding` already pushes
 *   the canonical `[MARCUS_ONBOARDING_V1_START]` control message through
 *   the existing onboarding pipeline.
 */
export type MarcusSlashCommandDispatchKind = "send-prompt" | "publish-onboarding-start";

export interface MarcusSlashCommand {
  /** Stable id, e.g. `staticads-adclone`. */
  id: string;
  /** Visible slash label including the leading slash, e.g. `/staticads-adclone`. */
  label: string;
  /** One-line description shown in the palette and the chat bubble. */
  description: string;
  /**
   * Concise, topic-relevant session title applied when this command is the
   * first turn of a fresh chat (e.g. "Static Ad Clone"). Lets slash-launched
   * sessions get a clean name instantly without an LLM round-trip. Omit to
   * skip auto-titling for this command.
   */
  sessionTitle?: string;
  /** Category grouping for the palette section header. */
  category: MarcusSlashCommandCategory;
  /** Short category label rendered as a chip in the palette row. */
  categoryLabel: string;
  /** Lucide icon component for the palette row. */
  icon: LucideIcon;
  /**
   * Hidden text sent to Marcus when the slash command fires. The user only
   * sees the rendered command bubble; Marcus reads this to decide which
   * `ads_dispatch_*` tool to call. Prompts are written to be deterministic
   * — they name the tool and any required arguments inline so Marcus does
   * not need to ask clarifying questions.
   *
   * Ignored when `dispatchKind === "publish-onboarding-start"` — that
   * variant bypasses Marcus entirely and drives the onboarding controller
   * directly.
   */
  hiddenPrompt: string;
  /** Source label shown above the bubble (e.g. "Slash command • Marcus"). */
  bubbleSource: string;
  /**
   * Search aliases. The palette matches the user's query against
   * `label` + `description` + these aliases. Optional.
   */
  aliases?: readonly string[];
  /**
   * How the slash command is dispatched on select. Defaults to
   * `send-prompt`. See {@link MarcusSlashCommandDispatchKind}.
   */
  dispatchKind?: MarcusSlashCommandDispatchKind;
}

const TRENDING_IMAGE_BRIEF =
  "Static ad for the user's active product. Use safe defaults — the user will refine the brief on the page.";

const TRENDING_VIDEO_BRIEF =
  "Short UGC-style video ad for the user's active product. Use safe defaults — the user will refine the brief on the page.";

const TEMPLATE_VIDEO_BRIEF =
  "Video ad from a template. Use safe defaults — the user will refine the brief on the page after picking a template.";

function slashCommandPrompt(
  label: string,
  tool: string,
  argsBlock: string,
  extraInstructions = "",
  afterToolInstructions = "Send a single short transition line (one sentence) acknowledging the flow opened — then wait for the user.",
): string {
  return [
    `[MARCUS_SLASH_V1] User triggered the slash command ${label}.`,
    `Call the tool \`${tool}\` immediately with the arguments below. Do not ask clarifying questions, do not narrate before the tool call, and do not call any other tool first.`,
    "",
    "Tool arguments:",
    argsBlock,
    extraInstructions ? `\nAdditional rules: ${extraInstructions.trim()}` : "",
    "",
    `After the tool returns the \`ads_guided_flow\` payload, the page will navigate on its own. ${afterToolInstructions}`,
  ]
    .join("\n")
    .trim();
}

export const MARCUS_ADS_SLASH_COMMANDS: readonly MarcusSlashCommand[] = [
  {
    id: "staticads-adclone",
    label: "/staticads-adclone",
    description: "Clone a winning static ad onto the active product.",
    sessionTitle: "Static Ad Clone",
    category: "static-ads",
    categoryLabel: "Static ads",
    icon: Copy,
    bubbleSource: "Slash command • Static ads",
    aliases: ["clone", "adclone", "static"],
    hiddenPrompt: slashCommandPrompt(
      "/staticads-adclone",
      "ads_dispatch_static_adclone",
      "(no arguments — pass an empty object so the guided UI handles defaults).",
      "Do not invent a product id. The page will ask the user to choose or add a product, click Find Image Ads after that choice, and open Agent Banan's chat once image production starts.",
    ),
  },
  {
    id: "staticads-trending",
    label: "/staticads-trending",
    description: "Open proven static-ad formats and prep the discovery flow.",
    sessionTitle: "Trending Static Ads",
    category: "static-ads",
    categoryLabel: "Static ads",
    icon: Sparkles,
    bubbleSource: "Slash command • Static ads",
    aliases: ["trending", "formats", "proven"],
    hiddenPrompt: slashCommandPrompt(
      "/staticads-trending",
      "ads_dispatch_trending_image_ad",
      `{ "brief": "${TRENDING_IMAGE_BRIEF}" }`,
      "Do not invent a product id — let the in-page picker resolve the active product.",
      'Read the returned prefill fields for product/context-aware recommendation metadata. If `prefill.productName` and `prefill.suggestedFormatTitle` exist, say: "For {product}, I’d start with {format}, but choose the format you like most, or tell me if you want another recommendation." If they do not exist, say: "Choose the format you like most, or if you want me to suggest one, let me know." Then wait for the user.',
    ),
  },
  {
    id: "videoads-trending",
    label: "/videoads-trending",
    description: "Open the video templates gallery to pick a template.",
    sessionTitle: "Video Ad Template",
    category: "video-ads",
    categoryLabel: "Video ads",
    icon: VideoIcon,
    bubbleSource: "Slash command • Video ads",
    aliases: ["templates", "template"],
    hiddenPrompt: slashCommandPrompt(
      "/videoads-trending",
      "ads_dispatch_template_video",
      `{ "brief": "${TEMPLATE_VIDEO_BRIEF}" }`,
      "Omit templateId so the gallery opens with no preselection and the user can pick a template card.",
    ),
  },
  {
    id: "videoads-ugc",
    label: "/videoads-ugc",
    description: "Browse trending UGC video formats and prep the flow.",
    sessionTitle: "Trending UGC Video",
    category: "video-ads",
    categoryLabel: "Video ads",
    icon: Flame,
    bubbleSource: "Slash command • Video ads",
    aliases: ["ugc", "trending", "creator"],
    hiddenPrompt: slashCommandPrompt(
      "/videoads-ugc",
      "ads_dispatch_trending_video_ad",
      `{ "brief": "${TRENDING_VIDEO_BRIEF}" }`,
      "Do not invent a product id — let the in-page picker resolve the active product.",
    ),
  },
  {
    id: "videoads-adclone",
    label: "/videoads-adclone",
    description: "Clone a winning video ad onto the active product.",
    sessionTitle: "Video Ad Clone",
    category: "video-ads",
    categoryLabel: "Video ads",
    icon: ImageIcon,
    bubbleSource: "Slash command • Video ads",
    aliases: ["clone", "adclone", "video"],
    hiddenPrompt: slashCommandPrompt(
      "/videoads-adclone",
      "ads_dispatch_video_adclone",
      "(no arguments — pass an empty object so the guided UI handles defaults).",
      "Do not invent a product id. The page will ask the user to choose or add a product, click Find Video Winners after that choice, ask the user to choose one returned source video, and stop before Build from this ad, extraction, or generation confirmation.",
    ),
  },
  {
    id: "onboarding",
    label: "/onboarding",
    description: "Replay the guided Marcus tour from the Explore page.",
    category: "onboarding",
    categoryLabel: "Tour",
    icon: Compass,
    bubbleSource: "Slash command • Onboarding",
    aliases: ["tour", "replay", "guided", "walkthrough", "onboard"],
    // The handler skips this prompt — `publish-onboarding-start` drives
    // the onboarding controller directly. Kept as a non-empty string so
    // accidental misuse via the standard path still routes sanely.
    hiddenPrompt:
      "[MARCUS_SLASH_V1] Frontend handles /onboarding directly via an internal event; no tool call is needed here.",
    dispatchKind: "publish-onboarding-start",
  },
];

const ALL_SLASH_COMMANDS_BY_ID = new Map(
  MARCUS_ADS_SLASH_COMMANDS.map((cmd) => [cmd.id, cmd] as const),
);

export function getMarcusSlashCommandById(id: string): MarcusSlashCommand | undefined {
  return ALL_SLASH_COMMANDS_BY_ID.get(id);
}

// First line of every `send-prompt` slash command, e.g.
// `[MARCUS_SLASH_V1] User triggered the slash command /staticads-adclone.`
const SLASH_PROMPT_LABEL_RE =
  /^\[MARCUS_SLASH_V1\] User triggered the slash command (\/[\w-]+)\./;

/**
 * Reconstruct the command-bubble display from a persisted hidden slash prompt.
 *
 * Live sends carry the display inline as a `data-companion-command` part, so
 * the pill renders without ever showing the prompt. But that part lives only
 * in local state — rehydrated history (page refresh, session switch) and the
 * `syncHistory` overwrite both bring the turn back as the raw
 * `[MARCUS_SLASH_V1] …` text. This maps that text back to the same pill so the
 * underlying prompt never leaks into the UI. Returns `null` for any text that
 * isn't a recognized slash prompt.
 */
export function parseSlashCommandDisplay(text: string): CompanionPromptDisplay | null {
  const match = SLASH_PROMPT_LABEL_RE.exec(text.trimStart());
  if (!match) return null;
  const cmd = getMarcusSlashCommandById(match[1].slice(1));
  if (!cmd) return null;
  return {
    kind: "command",
    label: cmd.label,
    description: cmd.description,
    source: cmd.bubbleSource,
  };
}

/**
 * Extract the slash query from the raw input value. Returns `null` when the
 * input is not a slash command (e.g. text doesn't start with `/`, or there
 * is whitespace before the slash, or the user has typed a space after the
 * command which means they're now writing free-form arguments).
 *
 * The query *includes* the leading slash so the caller can substring-match
 * against `command.label` directly.
 */
export function extractSlashQuery(rawInput: string): string | null {
  if (!rawInput.startsWith("/")) return null;
  // Once the user types whitespace, treat it as free-form text (not a slash
  // pick). This keeps the palette out of the way for `/staticads-adclone my
  // new mug` style follow-ups, which we may support later.
  if (/\s/.test(rawInput)) return null;
  return rawInput;
}

/**
 * Filter the slash command list by a `/foo`-style query. Empty `/` matches
 * all. Matching is case-insensitive against `label`, `description`, and
 * the optional `aliases` array.
 */
export function filterMarcusSlashCommands(
  query: string,
  commands: readonly MarcusSlashCommand[] = MARCUS_ADS_SLASH_COMMANDS,
): readonly MarcusSlashCommand[] {
  const trimmed = query.trim().toLowerCase();
  if (!trimmed || trimmed === "/") return commands;
  // Strip the leading slash so an alias like "trending" can match
  // when the user types "/tre".
  const needle = trimmed.startsWith("/") ? trimmed.slice(1) : trimmed;
  if (!needle) return commands;

  return commands.filter((cmd) => {
    const haystack = [cmd.label.slice(1), cmd.description, ...(cmd.aliases ?? [])]
      .join(" ")
      .toLowerCase();
    return haystack.includes(needle);
  });
}
